import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { App, Empty, Select, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import { _debounce } from 'helpers/debounce';

import { instance } from 'utils/api';
import SelectItem from './SelectItem';

const debounce = _debounce();

const SearchIcd: FC = () => {
  const { message } = App.useApp();
  const [icdData, setIcdData] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const controller = useRef<AbortController>();
  const { t } = useTranslation();

  const fetchIcdData = useCallback(
    async (term: string) => {
      if (controller.current) {
        controller.current.abort();
      }

      controller.current = new AbortController();

      try {
        const res = await instance.get(`icd?page=0&limit=10&term=${term}`, {
          signal: controller.current.signal
        });

        setIcdData(
          res.data.data.data.map(item => ({
            label: item?.title['@value'],
            value: item?._id
          }))
        );
      } catch (error) {
        if (axios.isAxiosError(error) && error?.response?.data) {
          message.error(error.response.data.message || 'Something went wrong');
        }
      } finally {
        setFetching(false);
      }
    },
    [message]
  );

  useEffect(() => {
    if (searchValue) {
      setFetching(true);
      debounce(() => fetchIcdData(searchValue));
    }
  }, [fetchIcdData, searchValue]);

  const onSearch = (value: string) => {
    setSearchValue(value);

    if (!value) {
      setIcdData([]);
    }
  };

  const onBlur = () => {
    setSearchValue('');
    setIcdData([]);
  };

  const renderNotFoundContent = () => {
    if (!searchValue) {
      return null;
    }

    if (fetching) {
      return (
        <Spin
          size="small"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 110
          }}
        />
      );
    }

    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  };

  return (
    <Select
      showSearch
      mode="multiple"
      placeholder={t('reports.report_icd_placeholder')}
      suffixIcon={<SearchOutlined style={{ fontSize: '18px' }} />}
      filterOption={false}
      searchValue={searchValue}
      onBlur={onBlur}
      onSearch={onSearch}
      size="large"
      optionLabelProp="label"
      notFoundContent={renderNotFoundContent()}
      optionRender={({ label, value }) => (
        <SelectItem label={label as string} value={value as string} />
      )}
      value={[]}
      options={icdData}
      style={{ width: '100%' }}
    />
  );
};

export default SearchIcd;
